import { useEffect, useState } from "react";
import { AlertCircle, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { AuthRedirectHandler } from "@/components/auth/AuthRedirectHandler";
import {
  challengePhoneMfa,
  getAuthenticatorAssuranceLevel,
  listPhoneFactors,
  verifyPhoneMfa,
} from "@/services/nativePhoneAuthService";

interface MfaChallengeFormProps {
  redirectPath: string;
}

export default function MfaChallengeForm({ redirectPath }: MfaChallengeFormProps) {
  const [checkedLevel, setCheckedLevel] = useState(false);
  const [requiresChallenge, setRequiresChallenge] = useState(false);
  const [factorId, setFactorId] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const checkLevel = async () => {
      try {
        const { data, error: levelError } = await getAuthenticatorAssuranceLevel();

        if (levelError) {
          throw levelError;
        }

        if (!active) {
          return;
        }

        setRequiresChallenge(data.nextLevel === "aal2" && data.currentLevel !== "aal2");
      } catch (caughtError) {
        if (!active) {
          return;
        }

        const message = caughtError instanceof Error ? caughtError.message : "Failed to check MFA status";
        setError(message);
      } finally {
        if (active) {
          setCheckedLevel(true);
        }
      }
    };

    void checkLevel();

    return () => {
      active = false;
    };
  }, []);

  const startChallenge = async () => {
    setBusy(true);
    setError(null);

    try {
      const factors = await listPhoneFactors();
      const phoneFactor = factors.find((factor) => factor.status === "verified") ?? factors[0];

      if (!phoneFactor) {
        throw new Error("No phone MFA factor is enrolled for this account.");
      }

      const { data, error: challengeError } = await challengePhoneMfa(phoneFactor.id);

      if (challengeError) {
        throw challengeError;
      }

      setFactorId(phoneFactor.id);
      setMaskedPhone(phoneFactor.phone);
      setChallengeId(data.id);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Failed to send MFA code";
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const submitChallenge = async () => {
    if (!factorId || !challengeId || code.length !== 6) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const { error: verifyError } = await verifyPhoneMfa(factorId, challengeId, code);

      if (verifyError) {
        throw verifyError;
      }

      setRequiresChallenge(false);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Failed to verify MFA code";
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  if (!checkedLevel) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-16">
        <p className="text-sm text-brand-slate">Checking security requirements...</p>
      </div>
    );
  }

  if (!requiresChallenge) {
    return <AuthRedirectHandler key={redirectPath} />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-mist px-4 py-16">
      <Card className="w-full max-w-md rounded-md border-brand-cloud shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-heading text-brand-ink">
            <ShieldCheck className="h-5 w-5" />
            Two-Factor Verification
          </CardTitle>
          <CardDescription className="text-brand-slate">
            Complete the WhatsApp code challenge to finish signing in.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Verification failed</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {maskedPhone && (
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
              Code destination: {maskedPhone}
            </div>
          )}

          {!challengeId ? (
            <Button className="w-full" disabled={busy} onClick={startChallenge} type="button">
              {busy ? "Sending code..." : "Send WhatsApp code"}
            </Button>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode}>
                  <InputOTPGroup className="gap-2">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPSlot key={index} index={index} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1" disabled={busy || code.length !== 6} onClick={submitChallenge} type="button">
                  {busy ? "Verifying..." : "Verify code"}
                </Button>
                <Button className="flex-1" disabled={busy} onClick={startChallenge} type="button" variant="outline">
                  Resend code
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
